import React, { useState, useCallback, useEffect, ReactElement, useRef, useMemo } from 'react';
import { Button, PhoneField, EmailField, TextField, ReyPopper } from '@framework-controls/core';
import { IView } from 'Framework';
import { strings } from 'src/localization';
import * as css from './MenuProfile.module.scss';
import classNames from 'classnames';
import { ComponentMapIds } from 'src/components/map';
import { ApptCust, UpdateApptCustInp, Customer, ascFormEngagementInput, ascFormSubmissionInput, VehicleRec, ascCtaInteractionInput } from 'src/models';
import SVPButton from 'src/components/SVPButtons/SVPButton';
import ErrorMessage from 'src/components/ErrorMessage/ErrorMessage';
import RecordLockError from 'src/components/RecordLockError/containers/RecordLockError';
import { ascCtaInteraction, ascFormEngagement, ascFormSubmission } from 'src/helpers/EventHelper/EventHelper';

const emailRegex = new RegExp(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))/.source +
    /@/.source +
    /((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.source);

export interface Iprops {
    openFlag: boolean;
    close: (e:any) => void;
    apptCust: ApptCust;
    pushToMainPanes: (inputView: IView) => void;
    onApptCustUpdate: (apptCust: UpdateApptCustInp) => void;
    clearError: () => void;
    changeCustInfo: (cust: Customer, profileNum, store: string, branch: string, previewFlag: string) => void;
    setChangeCustReturned: (value: boolean) => void;
    onCustUpdate: (cust: Customer) => void;
    checkUniqueEmail: (email: string, nameId: string, profileNum: string, store: string, branch: string,
        firstName: string, lastName: string, phone: string) => void;
    anchorEl: HTMLElement;
    theme: string;
    showProfileRef: React.RefObject<HTMLDivElement>
    allowLogin: boolean;
    custEmailUpdate: boolean;
    errMsg?: string;
    retCode?: string;
    specProfile: string;
    store: string;
    branch: string;
    customerInfo: Customer;
    previewFlag: string;
    changeCustReturned: boolean;
    profileOpen: boolean;
    identityVerified: boolean;
    mfaEnabled: boolean;
    vehicleList: VehicleRec[];
    selectedVehicle?: string;
    setProfileopen: (profileOpen: boolean) => void;
}

export interface IErrorDictionary {
    [field: string]: {
        error: boolean;
        helperText: string;
        isRequired?: boolean;
    }
}

const initialError: IErrorDictionary = {
    'menuFirstName': { error: false, helperText: '' },
    'menuLastName': { error: false, helperText: '' },
    'menuPhoneNum': { error: false, helperText: '' },
    'menuEmail': { error: false, helperText: '' },
};

const MenuProfile = (props: Iprops) => {
    const inpApptCust = props.apptCust;
    const [phoneNumber, setPhoneNumber] = useState(inpApptCust.custPhoneNumber);
    const [errors, setErrors] = useState(initialError);
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [emailCheck, setEmailCheck] = useState(false);
    const [errorUpdateFlag, setErrorUpdateFlag] = useState(false);
    const [initialEmail, setInitialEmail] = useState<string>('');
    const [phoneError, setPhoneError] = useState(false);
    const [identVerifyUpdt, setIdentVerifyUpdt] = useState(false);
    const [nameLengthError, setNameLengthError] = useState(false);
    let VarWidthDefault = '';
    if (props.anchorEl) {
        VarWidthDefault = window.getComputedStyle(props.anchorEl).width;
    }
    const [Varwidth, setWidth] = useState(VarWidthDefault);
    const noArrow = props.theme === 'porsche' || props.theme === 'honda' || props.theme === 'bmw' ? true : false;

    // force verify identity if mfa is enabled, the user is not verified, and the user is not a new customer
    const forceVerifyIdentity = !props.identityVerified && props.mfaEnabled && !!props.customerInfo.firstName;

    // repull menu slide btn width on window resize
    useEffect(() => {
        const handleResize = () => {
            setWidth(window.getComputedStyle(props.anchorEl).width);
        };
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [props.anchorEl]);

    // the following logic block is for handling the flyout top border line.
    let width2 = 'calc(100% - ' + Varwidth + ' - 2px)';
    const style = getComputedStyle(document.body);
    let varBordercolor = style.getPropertyValue('--IconTextColor');
    let varMargin = '0px';
    const varWidthNumber = parseFloat(Varwidth);
    if (varWidthNumber > 455.5) {
        width2 = '2px';
        varBordercolor = style.getPropertyValue('--menuSelected');
        varMargin = '-2px';
    }

    useEffect(() => {
        setFirstName(inpApptCust.custFirstName);
        setLastName(inpApptCust.custLastName);
        setPhoneNumber(inpApptCust.custPhoneNumber);
        setEmail(inpApptCust?.custEmailAddress ?? '');
        if (props.customerInfo) {
            setInitialEmail(props.customerInfo?.emailAddress ?? '');
        } else {
            setInitialEmail('');
        }
    }, [inpApptCust, setInitialEmail, props.customerInfo]);

    // Since the phone field does not contain an error prop, and the error is not
    // triggered until the user enters and leaves the field, we set focus to the
    // field and leave it when the screen is loaded to trigger the error in the field
    // if necessary.
    useEffect(() => {
        if (document.activeElement?.id !== 'menuPhoneNum_input' && phoneError) {
            setPhoneError(false);
            document.getElementById('menuPhoneNum_input')?.focus();
            setTimeout(() => {
                document.getElementById('menuPhoneNum_input')?.blur();
            }, 1,
            );
        }
    }, [phoneError]);

    useEffect(() => {
        if (phoneNumber?.length < 10) {
            setPhoneError(true);
        }
    }, [phoneNumber?.length]);

    // change the CSS style variable for --textColor but only for the ProfileFlyoutContent div
    // this change will only occur for audi.  NOTE.  Do not override CSS style variable with
    // aprroval.  This variable change has been approved by ZM
    useEffect(() => {
        if (props.theme === 'audi') {
            const flyoutContent = document.getElementById('ProfileFlyoutContent');
            if (flyoutContent) {
                flyoutContent.style.setProperty('--textColor', 'white');
            }
            // For Audi, change the primary color of the input fields to white
            const ids = ['menuFirstName', 'menuLastName', 'menuPhoneNum', 'menuEmail'];
            ids.forEach((id) => {
                const element = document.getElementById(id);
                if (element) {
                    element.style.setProperty('--primaryColor', 'white');
                }
            });
        }
    });

    const phoneFocusHandler = () => {
        focusHandler('menuPhoneNum', phoneNumber, false);
    };
    const emailFocusHandler = () => {
        focusHandler('menuEmail', email, false);
    };

    const focusHandler = useCallback((id: string, value: string = '', isError: boolean) => {
        let hasError = false;
        let helperText = '';
        let fieldErrors = { ...errors };
        let fieldCheck = false;
        try {
            const regCheck = value.search(/[*]/);
            if (regCheck !== -1) {
                fieldCheck = true;
            }
        } catch (e) {
            fieldCheck = true;
        }

        switch (id) {
            case 'menuLastName':
                if (fieldCheck) {
                    setLastName('');
                }
                if (!value.replace(/\s/g, '').length) {
                    hasError = true;
                    helperText = strings.formatString(strings.EmptyNameError, strings.LastNameLabel).toString();
                }
                break;

            case 'menuPhoneNum':
                hasError = isError;
                if (fieldCheck) {
                    setPhoneNumber('');
                }
                if (value.length < 10) {
                    hasError = true;
                }
                break;
            case 'menuEmail':
                if (fieldCheck) {
                    setEmail('');
                }
                if (value.length < 0) {
                    hasError = true;
                }
                break;
            default:
                break;
        }
        fieldErrors = {
            ...fieldErrors,
            [id]: {
                error: hasError,
                helperText: helperText,
            },
        };
        setErrors(fieldErrors);
    }, [errors]);

    // Field Validation
    const blurHandler = useCallback((id: string, value: string, isError: boolean) => {
        let hasError = false;
        let helperText = '';
        let fieldErrors = {...errors};
        let elementText = '';
        switch (id) {
            // First and last name combined cannot be greater than 35 characters in length.
            case 'menuFirstName':
                if ((value + lastName).length > 35) {
                    setNameLengthError(true);
                } else {
                    setNameLengthError(false);
                }
                if (!value.replace(/\s/g, '').length) {
                    hasError = true;
                    helperText = strings.formatString(strings.EmptyNameError, strings.FirstNameLabel).toString();
                }
                elementText = strings.FirstName;
                break;

            case 'menuLastName':
                if ((value + firstName).length > 35) {
                    setNameLengthError(true);
                } else {
                    setNameLengthError(false);
                }
                if (!value.replace(/\s/g, '').length) {
                    hasError = true;
                    helperText = strings.formatString(strings.EmptyNameError, strings.LastNameLabel).toString();
                }
                elementText = strings.LastName;
                break;

            case 'menuPhoneNum':
                hasError = isError;
                if (value.length < 10) {
                    hasError = true;
                }
                elementText = strings.PhoneNumber;
                break;
            case 'menuEmail':
                if (value.length < 0) {
                    hasError = true;
                }
                elementText = strings.EmailAddress;
                break;
            default:
                break;
        }
        fieldErrors = {
            ...fieldErrors,
            [id]: {
                error: hasError,
                helperText: helperText,
            },
        };
        setErrors(fieldErrors);
        const ascFormEngagementInp: ascFormEngagementInput = {
            pageType: 'contact',
            commType: 'form',
            commStatus: 'engage',
            elementText: elementText,
            department: 'service',
            formName: 'Service_Portal_2',
            formType: 'service_appointment',
        };
        ascFormEngagement(ascFormEngagementInp, props.vehicleList, props.selectedVehicle);
    }, [errors, firstName, lastName, props.vehicleList, props.selectedVehicle]);

    const disableProceed = !email?.match(emailRegex) || !phoneNumber || phoneNumber === '' ||
        firstName.length > 35 || lastName.length > 35 || phoneNumber.length < 10 || email.length > 62 ||
        errors['menuPhoneNum'].error || errors['menuFirstName'].error || errors['menuLastName'].error ||
        (firstName + lastName).length > 35;
        // !props.customerInfo.nameId || props.customerInfo.nameId === '';

    const firstNameChangedHandler = useCallback((id: string, value: string) => {
        setFirstName(value);
    }, []);

    const lastNameChangedHandler = useCallback((id: string, value: string) => {
        setLastName(value);
    }, []);

    const phoneNumberChangedHandler = useCallback((id: string, value: string) => {
        const updatedValue = value;
        setPhoneNumber(updatedValue);
    }, []);

    const emailChangedHandler = useCallback((id: string, value: string) => {
        setEmail(value);
        if (value.length > 0) {
            errors['menuEmail'].error = false;
        } else {
            errors['menuEmail'].error = true;
        }
    }, [errors]);

    const loginHandler = useCallback((e: any) => {
        props.pushToMainPanes({
            viewId: ComponentMapIds.Login,
            component: ComponentMapIds.Login,
        });
        props.close(e);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.Login,
            elementColor: '',
            elementOrder: '',
            elementType: 'popup',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicleList, props.selectedVehicle);
    }, [props.pushToMainPanes, props.close, props.vehicleList, props.selectedVehicle]);

    const cancelHandler = useCallback((e: any) => {
        props.close(e);
        setFirstName(inpApptCust.custFirstName);
        setLastName(inpApptCust.custLastName);
        setPhoneNumber(inpApptCust.custPhoneNumber);
        setEmail(inpApptCust.custEmailAddress);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.Cancel,
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicleList, props.selectedVehicle);
    }, [props.close, inpApptCust.custFirstName, inpApptCust.custLastName, props.vehicleList,
        inpApptCust.custPhoneNumber, inpApptCust.custEmailAddress, props.selectedVehicle]);

    const updateHandler = useCallback(() => {
        const apptCustInp: UpdateApptCustInp = {
            nameId: props.apptCust.custNameId,
            firstName: firstName,
            lastName: lastName,
            emailAddress: email,
            phoneNumber: phoneNumber,
            phoneType: props.apptCust.custPhoneType,
            prefContact: props.apptCust.custPrefContact,
            loggedIn: props.apptCust.custLoggedIn,
        };
        props.onApptCustUpdate(apptCustInp);
        // update customer record but only if customer exists
        if (props.customerInfo.nameId) {
            // remove phone number formatting to avoid PICK validation errors.
            const strippedPhone = phoneNumber?.replace(/[^0-9*]/g, '');
            const updateCustInp: Customer = {
                ...props.customerInfo,
                firstName: firstName,
                lastName: lastName,
                emailAddress: email,
                phoneNumber: strippedPhone,
            };
            props.setChangeCustReturned(false);
            props.changeCustInfo(updateCustInp, props.specProfile, props.store, props.branch, props.previewFlag);
        } else {
            props.close('');
        }
    }, [props.apptCust.custNameId, props.apptCust.custPhoneType, props.apptCust.custPrefContact, props.apptCust.custLoggedIn,
        props.onApptCustUpdate, props.customerInfo, props.close, props.setChangeCustReturned, props.changeCustInfo,
        props.specProfile, props.store, props.branch, props.previewFlag, firstName, lastName, email, phoneNumber]);

    useEffect(() => {
        if (props.identityVerified && !identVerifyUpdt) {
            setIdentVerifyUpdt(true);
            const apptCustInp: UpdateApptCustInp = {
                nameId: props.apptCust.custNameId,
                firstName: props.apptCust.custFirstName,
                lastName: props.customerInfo.lastName,
                emailAddress: props.customerInfo.emailAddress,
                phoneNumber: props.customerInfo.phoneNumber,
                phoneType: props.apptCust.custPhoneType,
                prefContact: props.apptCust.custPrefContact,
                loggedIn: props.apptCust.custLoggedIn,
            };
            props.onApptCustUpdate(apptCustInp);
        }
    }, [props.apptCust, props.customerInfo, props.identityVerified, props.onApptCustUpdate, identVerifyUpdt]);

    useEffect(() => {
        if (props.changeCustReturned) {
            let errMsg = props.errMsg;
            if (props.retCode === '3') {
                errMsg = '3';
            }
            if (!(errMsg && errMsg !== '0000')) {
                props.setChangeCustReturned(false);
                props.close('');
            }
        }
    }, [props.changeCustReturned, props.errMsg, props.retCode, props.close, props.setChangeCustReturned]);

    // Refs are always null in react test renderer, so in order to properly test we need to set a default ref value
    const initialValue: HTMLDivElement = document.createElement('div');
    const menuProfileRef = useRef<HTMLDivElement>(initialValue);

    const handleClickOutside = useCallback((e: any) => {
        if (!menuProfileRef.current?.contains(e.target as Element) &&
        !props.showProfileRef.current?.contains(e.target as Element) && props.retCode !== '3') {
            props.close(e);
            props.setProfileopen(false);
        }
    }, [props.close, props.setProfileopen, props.showProfileRef, props.retCode]);

    useEffect(() => {
        if (props.openFlag && !props.errMsg) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [props.openFlag, props.errMsg, handleClickOutside]);

    const signedInStyles = classNames(
        css.signedIn,
        {
            [css.signedInFord]: props.theme === 'ford',
        },
    );

    let loginBtn: ReactElement | null = null;
    if (props.allowLogin) {
        loginBtn = <SVPButton
            id='loginBtn'
            variant={'secondaryBtnAlt'}
            message={strings.LogIn}
            handler={(e) => loginHandler(e)}
            tooltip={strings.LogInTitle}
            theme={props.theme}
            noArrow={noArrow} />;
    }
    if (props.apptCust.custLoggedIn || props.customerInfo.login) {
        loginBtn = <div className={signedInStyles}>{strings.SignedIn}</div>;
    }

    const menuContentClasses = classNames(
        css.menuContent,
        {
            [css.menuMini]: props.theme === 'mini',
            [css.menuChevrolet]: props.theme === 'chevrolet',
            [css.menuBuick]: props.theme === 'buick',
            [css.menuMB]: props.theme === 'mercedesBenz',
        },
    );

    const inputClasses = classNames(
        css.input,
        {
            [css.inputAudi]: props.theme === 'audi',
            [css.inputBMW]: props.theme === 'bmw',
        },
    );

    const errorUpdateHandler = useCallback(() => {
        // if we are fine then we update as normal else we display error message
        // setEmail(inpApptCust.custEmailAddress);
        setEmail(initialEmail);
        setErrorUpdateFlag(true);
    }, [initialEmail, setEmail]);

    useEffect(() => {
        if (emailCheck && (errorUpdateFlag || props.errMsg==='0000')){
            updateHandler();
            setErrorUpdateFlag(false);
            props.clearError();
            setEmailCheck(false);
        }
    }, [props.clearError, props.errMsg, emailCheck, errorUpdateFlag, updateHandler]);

    const errorCancelHandler = useCallback(() => {
        props.clearError();
        setEmail(inpApptCust.custEmailAddress);
        setEmailCheck(false);
        setErrorUpdateFlag(false);
    }, [props.clearError, inpApptCust.custEmailAddress]);

    const checkUpdateHandler = useCallback((e: any) => {
        // check if email is used already
        // if (email !== inpApptCust.custEmailAddress) {
        if (email !== props.customerInfo.emailAddress) {
            props.checkUniqueEmail(email,
                props.apptCust.custNameId ? props.apptCust.custNameId : '',
                props.specProfile,
                props.store,
                props.branch,
                props.apptCust.custFirstName,
                props.apptCust.custLastName,
                props.apptCust.custPhoneNumber);
        } else {
            setErrorUpdateFlag(true);
        }
        setEmailCheck(true);
        const ascFormEngagementInp: ascFormEngagementInput = {
            pageType: 'contact',
            commType: 'form',
            commStatus: 'engage',
            elementText: strings.Update,
            department: 'service',
            formName: 'Service_Portal_2',
            formType: 'service_appointment',
        };
        ascFormEngagement(ascFormEngagementInp, props.vehicleList, props.selectedVehicle);

        const ascFormSubmissionInp: ascFormSubmissionInput = {
            pageType: 'service',
            commType: 'form',
            commOutcome: 'close',
            submissionId: props.customerInfo?.ascCustId! ?? '',
            elementText: strings.Update,
            department: 'service',
            formName: 'Service_Portal_2',
            formType: 'consumer_contact',
        };
        ascFormSubmission(ascFormSubmissionInp);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.Update,
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'other_buttons',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicleList, props.selectedVehicle);
        // if we are fine then we update as normal else we display error message
    }, [props.checkUniqueEmail, email, props.apptCust, props.specProfile,
        props.store, props.branch, props.customerInfo, props.vehicleList, props.selectedVehicle]);

    const recordLockTryAgain = useCallback((e: any) => {
        checkUpdateHandler(e);
    }, [checkUpdateHandler]);

    const viewEditHandler = useCallback(() => {
        props.setProfileopen(false);
        props.close('');
        props.pushToMainPanes({
            viewId: ComponentMapIds.VerifyIdentity,
            component: ComponentMapIds.VerifyIdentity,
        });
        const ascFormEngagementInp: ascFormEngagementInput = {
            pageType: 'contact',
            commType: 'form',
            commStatus: 'engage',
            elementText: strings.viewEdit,
            department: 'service',
            formName: 'Service_Portal_2',
            formType: 'service_appointment',
        };
        ascFormEngagement(ascFormEngagementInp, props.vehicleList, props.selectedVehicle);
    }, [props.pushToMainPanes, props.setProfileopen, props.close, props.vehicleList, props.selectedVehicle]);

    const error = useMemo(() => {
        let errMsg = props.errMsg;
        if (props.retCode === '3') {
            errMsg = '3';
        }
        if (props.retCode === '1009') {
            errMsg = '1009';
        }
        if (errMsg && errMsg !== '0000') {
            if (errMsg === '3') {
                return GetErrorMessage(errMsg, recordLockTryAgain, errorCancelHandler, errorCancelHandler, email);
            }

            return GetErrorMessage(errMsg, errorUpdateHandler, errorCancelHandler, errorCancelHandler, email);
        }

        return null;
    }, [props.errMsg, props.retCode, email, errorUpdateHandler, errorCancelHandler, recordLockTryAgain]);

    const menuProfileBtn = !forceVerifyIdentity ?
        <SVPButton
            id='updateBtn'
            variant={'primaryBtnAlt'}
            message={strings.Update}
            isDisabled={disableProceed}
            handler={checkUpdateHandler}
            tooltip={strings.UpdateTitle}
            theme={props.theme}
            noArrow={noArrow}
        /> :
        <SVPButton
            id='viewEdit'
            variant={'primaryBtnAlt'}
            message={strings.viewEdit}
            handler={viewEditHandler}
            tooltip={strings.UpdateTitle}
            theme={props.theme}
            noArrow={noArrow}
        />;

    const cancelLitClass = classNames(
        css.cancelLitDiv,
        {
            [css.cancelLitDivBMW]: props.theme === 'bmw',
            [css.cancelLitDivFord]: props.theme === 'ford',
            [css.cancelLitDivHonda]: props.theme === 'honda',
            [css.cancelLitDivIN]: props.theme === 'infiniti',
            [css.cancelLitDivMB]: props.theme === 'mercedesBenz',
            [css.cancelLitDivAcura]: props.theme === 'acura',
        },
    );

    return (
        <ReyPopper
            className={css.menuProfilePane}
            placement='bottom-end'
            anchorEl={props.anchorEl}
            isOpen={props.openFlag} transition
        >
            {error}
            <div id='ProfileFlyoutContent' className={menuContentClasses} ref={menuProfileRef}>
                <div className={css.container}>
                    <div className={css.toplineArea}>
                        <div className={css.toplineLeft}
                            style={{width: width2, borderColor: varBordercolor, marginLeft: varMargin}}>
                        </div>
                    </div>
                    <div className={css.btnArea} >
                        {loginBtn}
                    </div>
                    <div id='test2' className={css.inputArea}>
                        <div className={css.reqField}>{strings.RequiredFields} </div>
                        <div className={inputClasses}>
                            <TextField
                                id='menuFirstName'
                                label={strings.FirstName}
                                value={firstName}
                                size='full'
                                onChange={firstNameChangedHandler}
                                type='text'
                                onBlur={blurHandler}
                                error={errors['menuFirstName'].error || nameLengthError}
                                helperText={errors['menuFirstName'].error ?
                                    errors['menuFirstName'].helperText :
                                    nameLengthError ? strings.ValidNameLength : ''}
                                disabled={forceVerifyIdentity}
                            />
                        </div>
                        <div className={inputClasses}>
                            <TextField
                                id='menuLastName'
                                label={strings.LastName}
                                value={lastName}
                                size='full'
                                onChange={lastNameChangedHandler}
                                type='text'
                                onFocus={focusHandler}
                                onBlur = {blurHandler}
                                error={errors['menuLastName'].error || nameLengthError}
                                helperText={errors['menuLastName'].error ?
                                    errors['menuLastName'].helperText :
                                    nameLengthError ? strings.ValidNameLength : ''}
                                disabled={forceVerifyIdentity}
                            />
                        </div>
                        <div id='menuPhoneNumDiv' className={inputClasses} onFocus={phoneFocusHandler}>
                            <PhoneField
                                id='menuPhoneNum'
                                label={strings.PhoneNumber}
                                value={phoneNumber}
                                size='full'
                                onChange={phoneNumberChangedHandler}
                                onBlur = {blurHandler}
                                disabled={!props.identityVerified && props.mfaEnabled && !!props.customerInfo.nameId}
                            />
                        </div>
                        <div id='emailDiv' className={inputClasses} onFocus={emailFocusHandler}>
                            <EmailField
                                id='menuEmail'
                                label={strings.EmailAddress}
                                value={email}
                                size='full'
                                onChange={emailChangedHandler}
                                onBlur={blurHandler}
                                disabled={!props.custEmailUpdate || (forceVerifyIdentity)}
                                maxLength={62}
                            />
                        </div>
                    </div>
                    <div className={css.btnArea2}>
                        <div className={css.cancelDiv}>
                            <Button
                                variant={'text'}
                                id='cancelBtn'
                                onClick={cancelHandler}>
                                <div className={cancelLitClass}>{strings.Cancel}</div>
                            </Button>
                        </div>
                        <div className={css.updateDiv}>
                            {menuProfileBtn}
                        </div>
                    </div>
                </div>
            </div>
        </ReyPopper>
    );
};

function GetErrorMessage(
    message: string,
    updateBtnHandler: (e: any) => void,
    cancelBtnHandler: () => void,
    closeHandler: () => void,
    email: string) {
    let headerMsg = '';
    let errorMsg = '';
    let primaryMessage = '';
    let secondaryMessage = '';
    let primaryHandler = updateBtnHandler;
    let secondaryHandler = cancelBtnHandler;
    const messageMode = strings.MessageUpdating;

    switch (message) {
        case '3':
            return <RecordLockError
                headerMsg={strings.Oops}
                message={messageMode + strings.AcctTimeLit}
                primaryHandler={updateBtnHandler}
                secondaryHandler={cancelBtnHandler}
                closeHandler={cancelBtnHandler}
            />;
        case '1009':
            headerMsg = strings.Oops;
            errorMsg = strings.ChangeInfoErrMsg;
            primaryMessage = strings.OkLit.toUpperCase();
            primaryHandler = cancelBtnHandler;
            break;
        case '1007':
        default:
            headerMsg = strings.EmailUsed;
            errorMsg = strings.formatString(strings.EmailUsedMsg, email).toString();
            primaryMessage = strings.Update;
            secondaryMessage = strings.Cancel;
            primaryHandler = updateBtnHandler;
            secondaryHandler = cancelBtnHandler;
            break;
    }

    return (
        <ErrorMessage
            headerMsg={headerMsg}
            errorMsg={errorMsg}
            primaryBtnMessage={primaryMessage}
            secondaryBtnMessage={secondaryMessage ?? undefined}
            primaryHandler={primaryHandler}
            secondaryHandler={secondaryHandler ?? undefined}
            closeHandler={closeHandler}
        />
    );
}

export default MenuProfile;